import { useEffect, useMemo, useState } from "react";

import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import HandshakeIcon from "@mui/icons-material/Handshake";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getMyBurnoutRisk } from "@/api/burnout";
import { listSlotTemplates } from "@/api/catalog";
import {
  listCoverageRequests,
  reportAbsence,
  takeOverCoverage,
} from "@/api/coverage";
import { listOrganizations } from "@/api/organizations";
import type { AnonymizedAssignment } from "@/api/views";
import { getAnonymizedPlanning } from "@/api/views";
import type { SlotTemplate } from "@/api/types";
import { ClockingWidget } from "@/components/ClockingWidget";
import { HandoffDrawer } from "@/components/HandoffDrawer";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

function monthLabel(year: number, month: number, locale: string): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function dayOfWeek(year: number, month: number, day: number): number {
  const js = new Date(year, month - 1, day).getDay();
  return js === 0 ? 7 : js;
}

export function MyPlanningPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const [absenceTarget, setAbsenceTarget] = useState<AnonymizedAssignment | null>(null);
  const [absenceReason, setAbsenceReason] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [handoffTarget, setHandoffTarget] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });
  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const preferred =
        me?.active_org_id && orgs.data.items.find((o) => o.id === me.active_org_id)
          ? me.active_org_id
          : orgs.data.items[0]?.id ?? "";
      setOrgId(preferred);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  function navMonth(delta: number): void {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  const view = useQuery({
    queryKey: ["my-planning", orgId, year, month],
    queryFn: () => getAnonymizedPlanning(orgId, year, month),
    enabled: Boolean(orgId),
  });

  const slots = useQuery({
    queryKey: ["slot-templates", orgId, true],
    queryFn: () => (orgId ? listSlotTemplates(orgId, true) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });

  const coverageRequests = useQuery({
    queryKey: ["coverage-requests"],
    queryFn: listCoverageRequests,
    enabled: Boolean(me?.id),
    refetchInterval: 60_000,
  });

  const myBurnout = useQuery({
    queryKey: ["burnout-self"],
    queryFn: () => getMyBurnoutRisk(30),
    enabled: Boolean(me?.id),
    staleTime: 5 * 60_000,
  });

  const reportMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      reportAbsence(id, reason),
    onSuccess: (r) => {
      setFeedback(
        t("myPlanning.absenceReported", { count: r.candidates_notified }),
      );
      setAbsenceTarget(null);
      setAbsenceReason("");
      qc.invalidateQueries({ queryKey: ["my-planning"] });
      qc.invalidateQueries({ queryKey: ["coverage-requests"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      setTimeout(() => setFeedback(null), 5000);
    },
  });

  const takeOverMutation = useMutation({
    mutationFn: (id: string) => takeOverCoverage(id),
    onSuccess: () => {
      setFeedback(t("myPlanning.tookOver"));
      qc.invalidateQueries({ queryKey: ["my-planning"] });
      qc.invalidateQueries({ queryKey: ["coverage-requests"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      setTimeout(() => setFeedback(null), 5000);
    },
  });

  const assignmentsBySlot = useMemo(() => {
    const m = new Map<string, AnonymizedAssignment[]>();
    for (const a of view.data?.assignments ?? []) {
      const key = `${a.date}__${a.slot_template_id}`;
      const list = m.get(key) ?? [];
      list.push(a);
      m.set(key, list);
    }
    return m;
  }, [view.data]);

  const todayMineAssignmentId = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return (view.data?.assignments ?? []).find(
      (a) => a.is_mine && a.date === todayIso && !a.is_cancelled,
    )?.id;
  }, [view.data]);

  if (orgs.isLoading) return <CircularProgress />;
  if ((orgs.data?.items.length ?? 0) === 0) {
    return (
      <Box>
        <PageHeader title={t("myPlanning.title")} subtitle={t("myPlanning.subtitle")} helpTerm="monPlanning" />
        <Alert severity="info">{t("myPlanning.noOrgAccessible")}</Alert>
      </Box>
    );
  }

  const days = daysInMonth(year, month);
  const dayList = Array.from({ length: days }, (_, i) => i + 1);
  const slotList = (slots.data ?? []).filter((s) => s.is_active);
  const policy = view.data?.anonymization_policy ?? "INITIAL_ONLY";

  return (
    <Box>
      <PageHeader title={t("myPlanning.title")} subtitle={t("myPlanning.subtitle")} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("myPlanning.selectOrganization")}</InputLabel>
          <Select
            label={t("myPlanning.selectOrganization")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t("planning.previousMonth")}>
            <IconButton onClick={() => navMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ minWidth: 200, textAlign: "center", textTransform: "capitalize" }}
          >
            {monthLabel(year, month, i18n.language)}
          </Typography>
          <Tooltip title={t("planning.nextMonth")}>
            <IconButton onClick={() => navMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Chip
          size="small"
          color="default"
          variant="outlined"
          label={t(`myPlanning.policy.${policy}`)}
        />
      </Stack>

      {myBurnout.data &&
        (myBurnout.data.risk_level === "HIGH" ||
          myBurnout.data.risk_level === "CRITICAL") && (
          <Alert
            severity={myBurnout.data.risk_level === "CRITICAL" ? "error" : "warning"}
            sx={{ mb: 2 }}
          >
            {t("myPlanning.burnoutAlert", {
              level: t(`burnout.level.${myBurnout.data.risk_level}`),
              score: myBurnout.data.score,
              streak: myBurnout.data.max_consecutive_days,
            })}
          </Alert>
        )}

      <ClockingWidget todayAssignmentId={todayMineAssignmentId ?? null} />

      {/* Legend */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip size="small" sx={{ bgcolor: "primary.main", color: "primary.contrastText" }} label={t("myPlanning.legendMine")} />
        <Chip size="small" variant="outlined" label={t("myPlanning.legendOthers")} />
        <Chip size="small" variant="outlined" color="success" label={t("myPlanning.legendFree")} />
        <Chip size="small" color="error" variant="outlined" label={t("myPlanning.legendAbsent")} />
      </Stack>

      {feedback && <Alert severity="success" sx={{ mb: 2 }}>{feedback}</Alert>}

      {(coverageRequests.data ?? []).length > 0 && (
        <Card variant="outlined" sx={{ mb: 2, borderColor: "warning.main" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("myPlanning.coverageRequestsTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("myPlanning.coverageRequestsSubtitle")}
            </Typography>
            <Stack spacing={1}>
              {(coverageRequests.data ?? []).map((cr) => (
                <Paper key={cr.assignment.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {cr.organization_name} · {cr.slot_label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cr.assignment.date} · {cr.slot_start}–{cr.slot_end}
                        {cr.assignment.cancelled_reason
                          ? ` · ${cr.assignment.cancelled_reason}`
                          : ""}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={<HandshakeIcon />}
                      onClick={() => takeOverMutation.mutate(cr.assignment.id)}
                      disabled={takeOverMutation.isPending}
                    >
                      {t("myPlanning.takeOver")}
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {view.isLoading || slots.isLoading ? (
        <CircularProgress />
      ) : slotList.length === 0 ? (
        <Alert severity="info">{t("planning.noSlotTemplates")}</Alert>
      ) : isMobile ? (
        <GridMobile
          year={year}
          month={month}
          days={dayList}
          slots={slotList}
          assignmentsBySlot={assignmentsBySlot}
          onReportAbsence={setAbsenceTarget}
          onOpenHandoff={setHandoffTarget}
          t={t}
        />
      ) : (
        <GridDesktop
          year={year}
          month={month}
          days={dayList}
          slots={slotList}
          assignmentsBySlot={assignmentsBySlot}
          onReportAbsence={setAbsenceTarget}
          onOpenHandoff={setHandoffTarget}
          t={t}
        />
      )}

      <Dialog
        open={Boolean(absenceTarget)}
        onClose={() => setAbsenceTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("myPlanning.reportAbsenceTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {absenceTarget &&
              t("myPlanning.reportAbsenceBody", { date: absenceTarget.date })}
          </DialogContentText>
          <TextField
            label={t("myPlanning.absenceReasonLabel")}
            value={absenceReason}
            onChange={(e) => setAbsenceReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbsenceTarget(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<EventBusyIcon />}
            onClick={() => {
              if (absenceTarget)
                reportMutation.mutate({
                  id: absenceTarget.id,
                  reason: absenceReason,
                });
            }}
            disabled={reportMutation.isPending}
          >
            {t("myPlanning.confirmAbsence")}
          </Button>
        </DialogActions>
      </Dialog>

      <HandoffDrawer
        open={Boolean(handoffTarget)}
        assignmentId={handoffTarget}
        canAuthor
        onClose={() => setHandoffTarget(null)}
      />
    </Box>
  );
}


// ---------------------------------------------------------------------------
type GridProps = {
  year: number;
  month: number;
  days: number[];
  slots: SlotTemplate[];
  assignmentsBySlot: Map<string, AnonymizedAssignment[]>;
  onReportAbsence: (a: AnonymizedAssignment) => void;
  onOpenHandoff: (assignmentId: string) => void;
  t: (k: string) => string;
};

function GridDesktop({
  year,
  month,
  days,
  slots,
  assignmentsBySlot,
  onReportAbsence,
  onOpenHandoff,
  t,
}: GridProps) {
  return (
    <Paper variant="outlined" sx={{ overflow: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `100px repeat(${slots.length}, minmax(140px, 1fr))`,
          minWidth: 100 + slots.length * 140,
        }}
      >
        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }} />
        {slots.map((s) => (
          <Box
            key={s.id}
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderBottom: 1,
              borderColor: "divider",
              borderLeft: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
            </Typography>
          </Box>
        ))}
        {days.map((d) => {
          const date = ymd(year, month, d);
          const dow = dayOfWeek(year, month, d);
          const isWeekend = dow >= 6;
          return (
            <Box key={d} sx={{ display: "contents" }}>
              <Box
                sx={{
                  p: 1,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: isWeekend ? "action.hover" : "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 52,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{d}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`planning.weekday.${dow}`)}
                </Typography>
              </Box>
              {slots.map((s) => {
                const key = `${date}__${s.id}`;
                const list = assignmentsBySlot.get(key) ?? [];
                return (
                  <Cell
                    key={s.id}
                    list={list}
                    isWeekend={isWeekend}
                    onReportAbsence={onReportAbsence}
                    onOpenHandoff={onOpenHandoff}
                  />
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function GridMobile({
  year,
  month,
  days,
  slots,
  assignmentsBySlot,
  onReportAbsence,
  onOpenHandoff,
  t,
}: GridProps) {
  return (
    <Stack spacing={1.5}>
      {days.map((d) => {
        const date = ymd(year, month, d);
        const dow = dayOfWeek(year, month, d);
        const isWeekend = dow >= 6;
        return (
          <Paper
            key={d}
            variant="outlined"
            sx={{ p: 1.5, bgcolor: isWeekend ? "action.hover" : "transparent" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{pad2(d)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`planning.weekday.${dow}`)}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {slots.map((s) => {
                const key = `${date}__${s.id}`;
                const list = assignmentsBySlot.get(key) ?? [];
                return (
                  <Box key={s.id} sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
                      </Typography>
                    </Stack>
                    <CellInner list={list} onReportAbsence={onReportAbsence} onOpenHandoff={onOpenHandoff} />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}


function Cell({
  list,
  isWeekend,
  onReportAbsence,
  onOpenHandoff,
}: {
  list: AnonymizedAssignment[];
  isWeekend: boolean;
  onReportAbsence: (a: AnonymizedAssignment) => void;
  onOpenHandoff: (assignmentId: string) => void;
}) {
  return (
    <Box
      sx={{
        p: 0.75,
        borderTop: 1,
        borderLeft: 1,
        borderColor: "divider",
        minHeight: 52,
        bgcolor: isWeekend ? "action.hover" : "transparent",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <CellInner list={list} onReportAbsence={onReportAbsence} onOpenHandoff={onOpenHandoff} />
    </Box>
  );
}

function CellInner({
  list,
  onReportAbsence,
  onOpenHandoff,
}: {
  list: AnonymizedAssignment[];
  onReportAbsence: (a: AnonymizedAssignment) => void;
  onOpenHandoff: (assignmentId: string) => void;
}) {
  const { t } = useTranslation();
  if (list.length === 0) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        label={t("myPlanning.free")}
        sx={{ alignSelf: "flex-start" }}
      />
    );
  }
  return (
    <>
      {list.map((a) => {
        if (a.is_cancelled) {
          return (
            <Tooltip key={a.id} title={t("myPlanning.absentTooltip")} placement="top">
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`✗ ${a.masked_label}`}
                sx={{ textDecoration: "line-through", opacity: 0.7 }}
              />
            </Tooltip>
          );
        }
        if (a.is_mine) {
          return (
            <Stack key={a.id} direction="row" alignItems="center" spacing={0.25}>
              <Tooltip title={t("myPlanning.clickToReportAbsence")} placement="top">
                <Chip
                  size="small"
                  clickable
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                  label={a.masked_label}
                  onClick={() => onReportAbsence(a)}
                />
              </Tooltip>
              <CopyIdButton id={a.id} />
              <HandoffButton id={a.id} onOpen={onOpenHandoff} />
            </Stack>
          );
        }
        return (
          <Chip
            key={a.id}
            size="small"
            variant="outlined"
            sx={{ color: "text.secondary" }}
            label={a.masked_label}
          />
        );
      })}
    </>
  );
}


function CopyIdButton({ id }: { id: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API can fail on insecure contexts — silently no-op
    }
  };
  return (
    <Tooltip
      title={copied ? t("myPlanning.idCopied") : t("myPlanning.copyId")}
      placement="top"
    >
      <IconButton
        size="small"
        onClick={copy}
        aria-label={t("myPlanning.copyId")}
        sx={{ p: 0.25 }}
      >
        {copied ? (
          <CheckIcon fontSize="inherit" sx={{ fontSize: 14, color: "success.main" }} />
        ) : (
          <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14, opacity: 0.55 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}


function HandoffButton({
  id,
  onOpen,
}: {
  id: string;
  onOpen: (assignmentId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("handoff.openTooltip")} placement="top">
      <IconButton
        size="small"
        onClick={() => onOpen(id)}
        aria-label={t("handoff.openTooltip")}
        sx={{ p: 0.25 }}
      >
        <AssignmentTurnedInIcon
          fontSize="inherit"
          sx={{ fontSize: 14, opacity: 0.55, color: "primary.contrastText" }}
        />
      </IconButton>
    </Tooltip>
  );
}
